import {Unit} from './units';

// Supported language keys (following BCP 47 standard for HTML lang attribute)
export type Lang = 'url' | 'abbr' | 'en' | 'zh' | 'zh-TW' | 'fr' | 'es' | 'pt' | 'ja' | 'ko' | 'hi' | 'ru';

// Localization labels and unit display names
export const i18n: Record<Lang, {
    cardTitle: string;
    metricLabel: string;
    imperialLabel: string;
    langLabel: string;
    tabs: Record<string, string>;
    unitLabels: Record<Unit, string>;
    infoCard: {
        title: string;
        description: string;
        github?: string;
        githubLabel?: string;
    };
}> = {
    url: {
        cardTitle: 'Conv',
        metricLabel: 'M',
        imperialLabel: 'I',
        langLabel: 'L',
        tabs: {
            length: 'len',
            weight: 'wgt',
            volume: 'vol',
            area: 'ar',
            speed: 'spd',
            temperature: 'tmp',
        },
        unitLabels: {
            centimeters: 'cm',
            meters: 'm',
            kilometers: 'km',
            inch: 'in',
            foot: 'ft',
            yard: 'yd',
            mile: 'mi',
            grams: 'g',
            kilograms: 'kg',
            ounce: 'oz',
            pound: 'lb',
            liters: 'l',
            milliliters: 'ml',
            gallon: 'gal',
            fluid_ounce: 'floz',
            pint: 'pt',
            quart: 'qt',
            celsius: 'c',
            fahrenheit: 'f',
            square_meters: 'sqm',
            hectares: 'ha',
            square_kilometers: 'sqkm',
            square_feet: 'sqft',
            acres: 'ac',
            square_miles: 'sqmi',
            kilometers_per_hour: 'kmh',
            miles_per_hour: 'mph',
            meters_per_second: 'mps',
            feet_per_second: 'ftps',
            knots: 'kn',
        },
        infoCard: {
            title: 'Tips',
            description: 'Convert between metric and imperial units. Select tabs to convert different types. Real-time bidirectional conversion.',
        },
    },
    abbr: {
        cardTitle: 'Conv',
        metricLabel: 'M',
        imperialLabel: 'I',
        langLabel: 'L',
        tabs: {
            length: 'Len',
            weight: 'Wgt',
            volume: 'Vol',
            area: 'Ar',
            speed: 'Spd',
            temperature: 'Tmp',
        },
        unitLabels: {
            centimeters: 'cm',
            meters: 'm',
            kilometers: 'km',
            inch: 'in',
            foot: 'ft',
            yard: 'yd',
            mile: 'mi',
            grams: 'g',
            kilograms: 'kg',
            ounce: 'oz',
            pound: 'lb',
            liters: 'L',
            milliliters: 'mL',
            gallon: 'gal',
            fluid_ounce: 'fl oz',
            pint: 'pt',
            quart: 'qt',
            celsius: '°C',
            fahrenheit: '°F',
            square_meters: 'm²',
            hectares: 'ha',
            square_kilometers: 'km²',
            square_feet: 'ft²',
            acres: 'ac',
            square_miles: 'mi²',
            kilometers_per_hour: 'km/h',
            miles_per_hour: 'mph',
            meters_per_second: 'm/s',
            feet_per_second: 'ft/s',
            knots: 'kn',
        },
        infoCard: {
            title: 'Tips',
            description: 'Convert between metric and imperial units. Select tabs to convert different types. Real-time bidirectional conversion.',
        },
    },
    en: {
        cardTitle: 'Unit Converter',
        metricLabel: 'Metric',
        imperialLabel: 'Imperial',
        langLabel: 'Language',
        tabs: {
            length: 'Length / Distance',
            weight: 'Weight / Mass',
            volume: 'Volume',
            area: 'Area',
            speed: 'Speed',
            temperature: 'Temperature',
        },
        unitLabels: {
            centimeters: 'Centimeters (cm)',
            meters: 'Meters (m)',
            kilometers: 'Kilometers (km)',
            inch: 'Inch (in)',
            foot: 'Foot (ft)',
            yard: 'Yard (yd)',
            mile: 'Mile (mi)',
            grams: 'Grams (g)',
            kilograms: 'Kilograms (kg)',
            ounce: 'Ounce (oz)',
            pound: 'Pound (lb)',
            liters: 'Liters (L)',
            milliliters: 'Milliliters (mL)',
            gallon: 'Gallon (gal)',
            fluid_ounce: 'Fluid Ounce (fl oz)',
            pint: 'Pint (pt)',
            quart: 'Quart (qt)',
            celsius: 'Celsius (°C)',
            fahrenheit: 'Fahrenheit (°F)',
            square_meters: 'Square Meters (m²)',
            hectares: 'Hectares (ha)',
            square_kilometers: 'Square Kilometers (km²)',
            square_feet: 'Square Feet (ft²)',
            acres: 'Acres (ac)',
            square_miles: 'Square Miles (mi²)',
            kilometers_per_hour: 'Kilometers per Hour (km/h)',
            miles_per_hour: 'Miles per Hour (mph)',
            meters_per_second: 'Meters per Second (m/s)',
            feet_per_second: 'Feet per Second (ft/s)',
            knots: 'Knots (kn)',
        },
        infoCard: {
            title: 'Conversion Tips',
            description: 'This app helps you accurately convert between metric and imperial units. Select different category tabs to convert length, weight, volume, temperature, area, and speed units. Real-time bidirectional conversion allows you to input values on either side and automatically displays conversion results on the other side.',
            github: 'https://github.com/muhac/what-the-imperial-units',
            githubLabel: 'GitHub Repository:',
        },
    },
    fr: {
        cardTitle: 'Convertisseur',
        metricLabel: 'Métrique',
        imperialLabel: 'Impérial',
        langLabel: 'Langue',
        tabs: {
            length: 'Longueur / Distance',
            weight: 'Poids / Masse',
            volume: 'Volume',
            area: 'Surface',
            speed: 'Vitesse',
            temperature: 'Température',
        },
        unitLabels: {
            centimeters: 'Centimètres (cm)',
            meters: 'Mètres (m)',
            kilometers: 'Kilomètres (km)',
            inch: 'Pouce (in)',
            foot: 'Pied (ft)',
            yard: 'Yard (yd)',
            mile: 'Mille (mi)',
            grams: 'Grammes (g)',
            kilograms: 'Kilogrammes (kg)',
            ounce: 'Once (oz)',
            pound: 'Livre (lb)',
            liters: 'Litres (L)',
            milliliters: 'Millilitres (mL)',
            gallon: 'Gallon (gal)',
            fluid_ounce: 'Once liquide (fl oz)',
            pint: 'Pinte (pt)',
            quart: 'Quart (qt)',
            celsius: 'Celsius (°C)',
            fahrenheit: 'Fahrenheit (°F)',
            square_meters: 'Mètres carrés (m²)',
            hectares: 'Hectares (ha)',
            square_kilometers: 'Kilomètres carrés (km²)',
            square_feet: 'Pieds carrés (ft²)',
            acres: 'Acres (ac)',
            square_miles: 'Milles carrés (mi²)',
            kilometers_per_hour: 'Kilomètres par heure (km/h)',
            miles_per_hour: 'Miles par heure (mph)',
            meters_per_second: 'Mètres par seconde (m/s)',
            feet_per_second: 'Pieds par seconde (ft/s)',
            knots: 'Nœuds (kn)',
        },
        infoCard: {
            title: 'Conseils de conversion',
            description: 'Cette application vous aide à convertir avec précision entre les unités métriques et impériales. Sélectionnez différents onglets de catégorie pour convertir les unités de longueur, poids, volume, température, superficie et vitesse. La conversion bidirectionnelle en temps réel vous permet de saisir des valeurs de chaque côté et affiche automatiquement les résultats de conversion de l\'autre côté.',
            github: 'https://github.com/muhac/what-the-imperial-units',
            githubLabel: 'Dépôt GitHub :',
        },
    },
    es: {
        cardTitle: 'Convertidor de Unidades',
        metricLabel: 'Métrico',
        imperialLabel: 'Imperial',
        langLabel: 'Idioma',
        tabs: {
            length: 'Longitud / Distancia',
            weight: 'Peso / Masa',
            volume: 'Volumen',
            area: 'Área',
            speed: 'Velocidad',
            temperature: 'Temperatura',
        },
        unitLabels: {
            centimeters: 'Centímetros (cm)',
            meters: 'Metros (m)',
            kilometers: 'Kilómetros (km)',
            inch: 'Pulgada (in)',
            foot: 'Pie (ft)',
            yard: 'Yarda (yd)',
            mile: 'Milla (mi)',
            grams: 'Gramos (g)',
            kilograms: 'Kilogramos (kg)',
            ounce: 'Onza (oz)',
            pound: 'Libra (lb)',
            liters: 'Litros (L)',
            milliliters: 'Mililitros (mL)',
            gallon: 'Galón (gal)',
            fluid_ounce: 'Onza líquida (fl oz)',
            pint: 'Pinta (pt)',
            quart: 'Cuarto (qt)',
            celsius: 'Celsius (°C)',
            fahrenheit: 'Fahrenheit (°F)',
            square_meters: 'Metros cuadrados (m²)',
            hectares: 'Hectáreas (ha)',
            square_kilometers: 'Kilómetros cuadrados (km²)',
            square_feet: 'Pies cuadrados (ft²)',
            acres: 'Acres (ac)',
            square_miles: 'Millas cuadradas (mi²)',
            kilometers_per_hour: 'Kilómetros por hora (km/h)',
            miles_per_hour: 'Millas por hora (mph)',
            meters_per_second: 'Metros por segundo (m/s)',
            feet_per_second: 'Pies por segundo (ft/s)',
            knots: 'Nudos (kn)',
        },
        infoCard: {
            title: 'Consejos de conversión',
            description: 'Esta aplicación te ayuda a convertir con precisión entre unidades métricas e imperiales. Selecciona diferentes pestañas de categoría para convertir unidades de longitud, peso, volumen, temperatura, área y velocidad. La conversión bidireccional en tiempo real te permite ingresar valores en cualquier lado y mostrar automáticamente los resultados de conversión en el otro lado.',
            github: 'https://github.com/muhac/what-the-imperial-units',
            githubLabel: 'Repositorio GitHub:',
        },
    },
    zh: {
        cardTitle: '单位转换器',
        metricLabel: '公制',
        imperialLabel: '英制',
        langLabel: '语言',
        tabs: {
            length: '长度 · 距离',
            weight: '重量 · 质量',
            volume: '体积',
            area: '面积',
            speed: '速度',
            temperature: '温度',
        },
        unitLabels: {
            centimeters: '厘米 (cm)',
            meters: '米 (m)',
            kilometers: '千米 (km)',
            inch: '英寸 (in)',
            foot: '英尺 (ft)',
            yard: '码 (yd)',
            mile: '英里 (mi)',
            grams: '克 (g)',
            kilograms: '千克 (kg)',
            ounce: '盎司 (oz)',
            pound: '磅 (lb)',
            liters: '升 (L)',
            milliliters: '毫升 (mL)',
            gallon: '加仑 (gal)',
            fluid_ounce: '液体盎司 (fl oz)',
            pint: '品脱 (pt)',
            quart: '夸脱 (qt)',
            celsius: '摄氏度 (°C)',
            fahrenheit: '华氏度 (°F)',
            square_meters: '平方米 (m²)',
            hectares: '公顷 (ha)',
            square_kilometers: '平方千米 (km²)',
            square_feet: '平方英尺 (ft²)',
            acres: '英亩 (ac)',
            square_miles: '平方英里 (mi²)',
            kilometers_per_hour: '千米 / 小时 (km/h)',
            miles_per_hour: '英里 / 小时 (mph)',
            meters_per_second: '米 / 秒 (m/s)',
            feet_per_second: '英尺 / 秒 (ft/s)',
            knots: '节 (kn)',
        },
        infoCard: {
            title: '转换提示',
            description: '这个应用帮助您在公制和英制单位之间进行精确转换。选择不同的类别标签来转换长度、重量、体积、温度、面积或速度单位。实时双向转换让您可以在任一侧输入数值，另一侧会自动显示转换结果。',
            github: 'https://github.com/muhac/what-the-imperial-units',
            githubLabel: 'GitHub 仓库地址：',
        },
    },
    'zh-TW': {
        cardTitle: '單位轉換器',
        metricLabel: '公制',
        imperialLabel: '英制',
        langLabel: '語言',
        tabs: {
            length: '長度 · 距離',
            weight: '重量 · 質量',
            volume: '體積',
            area: '面積',
            speed: '速度',
            temperature: '溫度',
        },
        unitLabels: {
            centimeters: '公分 (cm)',
            meters: '公尺 (m)',
            kilometers: '公里 (km)',
            inch: '英吋 (in)',
            foot: '英尺 (ft)',
            yard: '碼 (yd)',
            mile: '英里 (mi)',
            grams: '公克 (g)',
            kilograms: '公斤 (kg)',
            ounce: '盎司 (oz)',
            pound: '磅 (lb)',
            liters: '公升 (L)',
            milliliters: '毫升 (mL)',
            gallon: '加侖 (gal)',
            fluid_ounce: '液體盎司 (fl oz)',
            pint: '品脫 (pt)',
            quart: '夸脫 (qt)',
            celsius: '攝氏度 (°C)',
            fahrenheit: '華氏度 (°F)',
            square_meters: '平方公尺 (m²)',
            hectares: '公頃 (ha)',
            square_kilometers: '平方公里 (km²)',
            square_feet: '平方英尺 (ft²)',
            acres: '英畝 (ac)',
            square_miles: '平方英里 (mi²)',
            kilometers_per_hour: '公里 / 小時 (km/h)',
            miles_per_hour: '英里 / 小時 (mph)',
            meters_per_second: '公尺 / 秒 (m/s)',
            feet_per_second: '英尺 / 秒 (ft/s)',
            knots: '節 (kn)',
        },
        infoCard: {
            title: '轉換提示',
            description: '這個應用程式幫助您在公制和英制單位之間進行精確轉換。選擇不同的類別標籤來轉換長度、重量、體積、溫度、面積或速度單位。即時雙向轉換讓您可以在任一側輸入數值，另一側會自動顯示轉換結果。',
            github: 'https://github.com/muhac/what-the-imperial-units',
            githubLabel: 'GitHub 倉庫地址：',
        },
    },
    ja: {
        cardTitle: '単位変換',
        metricLabel: 'メートル法',
        imperialLabel: 'ヤード・ポンド法',
        langLabel: '言語',
        tabs: {
            length: '長さ · 距離',
            weight: '重さ · 質量',
            volume: '体積',
            area: '面積',
            speed: '速度',
            temperature: '温度',
        },
        unitLabels: {
            centimeters: 'センチメートル (cm)',
            meters: 'メートル (m)',
            kilometers: 'キロメートル (km)',
            inch: 'インチ (in)',
            foot: 'フィート (ft)',
            yard: 'ヤード (yd)',
            mile: 'マイル (mi)',
            grams: 'グラム (g)',
            kilograms: 'キログラム (kg)',
            ounce: 'オンス (oz)',
            pound: 'ポンド (lb)',
            liters: 'リットル (L)',
            milliliters: 'ミリリットル (mL)',
            gallon: 'ガロン (gal)',
            fluid_ounce: '液量オンス (fl oz)',
            pint: 'パイント (pt)',
            quart: 'クォート (qt)',
            celsius: 'セルシウス (°C)',
            fahrenheit: '華氏 (°F)',
            square_meters: '平方メートル (m²)',
            hectares: 'ヘクタール (ha)',
            square_kilometers: '平方キロメートル (km²)',
            square_feet: '平方フィート (ft²)',
            acres: 'エーカー (ac)',
            square_miles: '平方マイル (mi²)',
            kilometers_per_hour: 'キロメートル毎時 (km/h)',
            miles_per_hour: 'マイル毎時 (mph)',
            meters_per_second: 'メートル毎秒 (m/s)',
            feet_per_second: 'フィート毎秒 (ft/s)',
            knots: 'ノット (kn)',
        },
        infoCard: {
            title: '変換のヒント',
            description: 'このアプリは、メートル法とヤード・ポンド法の単位を正確に変換するのに役立ちます。異なるカテゴリタブを選択して、長さ、重量、体積、温度、面積、速度の単位を変換できます。リアルタイム双方向変換により、どちらの側に数値を入力しても、もう一方の側に変換結果が自動的に表示されます。',
            github: 'https://github.com/muhac/what-the-imperial-units',
            githubLabel: 'GitHub リポジトリ：',
        },
    },
    ko: {
        cardTitle: '단위 변환기',
        metricLabel: '미터법',
        imperialLabel: '야드파운드법',
        langLabel: '언어',
        tabs: {
            length: '길이 · 거리',
            weight: '무게 · 질량',
            volume: '부피',
            area: '면적',
            speed: '속도',
            temperature: '온도',
        },
        unitLabels: {
            centimeters: '센티미터 (cm)',
            meters: '미터 (m)',
            kilometers: '킬로미터 (km)',
            inch: '인치 (in)',
            foot: '피트 (ft)',
            yard: '야드 (yd)',
            mile: '마일 (mi)',
            grams: '그램 (g)',
            kilograms: '킬로그램 (kg)',
            ounce: '온스 (oz)',
            pound: '파운드 (lb)',
            liters: '리터 (L)',
            milliliters: '밀리리터 (mL)',
            gallon: '갤런 (gal)',
            fluid_ounce: '액량 온스 (fl oz)',
            pint: '파인트 (pt)',
            quart: '쿼트 (qt)',
            celsius: '섭씨 (°C)',
            fahrenheit: '화씨 (°F)',
            square_meters: '제곱미터 (m²)',
            hectares: '헥타르 (ha)',
            square_kilometers: '제곱킬로미터 (km²)',
            square_feet: '제곱피트 (ft²)',
            acres: '에이커 (ac)',
            square_miles: '제곱마일 (mi²)',
            kilometers_per_hour: '킬로미터/시간 (km/h)',
            miles_per_hour: '마일/시간 (mph)',
            meters_per_second: '미터/초 (m/s)',
            feet_per_second: '피트/초 (ft/s)',
            knots: '노트 (kn)',
        },
        infoCard: {
            title: '변환 팁',
            description: '이 앱은 미터법과 야드파운드법 단위 간의 정확한 변환을 도와줍니다. 다양한 카테고리 탭을 선택하여 길이, 무게, 부피, 온도, 면적, 속도 단위를 변환할 수 있습니다. 실시간 양방향 변환으로 어느 쪽에 숫자를 입력하든 다른 쪽에 변환 결과가 자동으로 표시됩니다.',
            github: 'https://github.com/muhac/what-the-imperial-units',
            githubLabel: 'GitHub 저장소:',
        },
    },
    pt: {
        cardTitle: 'Conversor de Unidades',
        metricLabel: 'Métrico',
        imperialLabel: 'Imperial',
        langLabel: 'Idioma',
        tabs: {
            length: 'Comprimento / Distância',
            weight: 'Peso / Massa',
            volume: 'Volume',
            area: 'Área',
            speed: 'Velocidade',
            temperature: 'Temperatura',
        },
        unitLabels: {
            centimeters: 'Centímetros (cm)',
            meters: 'Metros (m)',
            kilometers: 'Quilômetros (km)',
            inch: 'Polegada (in)',
            foot: 'Pé (ft)',
            yard: 'Jarda (yd)',
            mile: 'Milha (mi)',
            grams: 'Gramas (g)',
            kilograms: 'Quilogramas (kg)',
            ounce: 'Onça (oz)',
            pound: 'Libra (lb)',
            liters: 'Litros (L)',
            milliliters: 'Mililitros (mL)',
            gallon: 'Galão (gal)',
            fluid_ounce: 'Onça líquida (fl oz)',
            pint: 'Pinta (pt)',
            quart: 'Quarto (qt)',
            celsius: 'Celsius (°C)',
            fahrenheit: 'Fahrenheit (°F)',
            square_meters: 'Metros quadrados (m²)',
            hectares: 'Hectares (ha)',
            square_kilometers: 'Quilômetros quadrados (km²)',
            square_feet: 'Pés quadrados (ft²)',
            acres: 'Acres (ac)',
            square_miles: 'Milhas quadradas (mi²)',
            kilometers_per_hour: 'Quilômetros por hora (km/h)',
            miles_per_hour: 'Milhas por hora (mph)',
            meters_per_second: 'Metros por segundo (m/s)',
            feet_per_second: 'Pés por segundo (ft/s)',
            knots: 'Nós (kn)',
        },
        infoCard: {
            title: 'Dicas de Conversão',
            description: 'Este aplicativo ajuda você a converter com precisão entre unidades métricas e imperiais. Selecione diferentes abas de categoria para converter unidades de comprimento, peso, volume, temperatura, área e velocidade. A conversão bidirecional em tempo real permite que você insira valores em qualquer lado e exiba automaticamente os resultados de conversão do outro lado.',
            github: 'https://github.com/muhac/what-the-imperial-units',
            githubLabel: 'Repositório GitHub:',
        },
    },
    hi: {
        cardTitle: 'यूनिट कन्वर्टर',
        metricLabel: 'मेट्रिक',
        imperialLabel: 'इम्पीरियल',
        langLabel: 'भाषा',
        tabs: {
            length: 'लंबाई / दूरी',
            weight: 'वजन / द्रव्यमान',
            volume: 'आयतन',
            area: 'क्षेत्रफल',
            speed: 'गति',
            temperature: 'तापमान',
        },
        unitLabels: {
            centimeters: 'सेंटीमीटर (cm)',
            meters: 'मीटर (m)',
            kilometers: 'किलोमीटर (km)',
            inch: 'इंच (in)',
            foot: 'फुट (ft)',
            yard: 'यार्ड (yd)',
            mile: 'मील (mi)',
            grams: 'ग्राम (g)',
            kilograms: 'किलोग्राम (kg)',
            ounce: 'औंस (oz)',
            pound: 'पाउंड (lb)',
            liters: 'लीटर (L)',
            milliliters: 'मिलीलीटर (mL)',
            gallon: 'गैलन (gal)',
            fluid_ounce: 'द्रव औंस (fl oz)',
            pint: 'पाइंट (pt)',
            quart: 'क्वार्ट (qt)',
            celsius: 'सेल्सियस (°C)',
            fahrenheit: 'फारेनहाइट (°F)',
            square_meters: 'वर्ग मीटर (m²)',
            hectares: 'हेक्टेयर (ha)',
            square_kilometers: 'वर्ग किलोमीटर (km²)',
            square_feet: 'वर्ग फुट (ft²)',
            acres: 'एकड़ (ac)',
            square_miles: 'वर्ग मील (mi²)',
            kilometers_per_hour: 'किलोमीटर प्रति घंटा (km/h)',
            miles_per_hour: 'मील प्रति घंटा (mph)',
            meters_per_second: 'मीटर प्रति सेकंड (m/s)',
            feet_per_second: 'फुट प्रति सेकंड (ft/s)',
            knots: 'नॉट्स (kn)',
        },
        infoCard: {
            title: 'रूपांतरण सुझाव',
            description: 'यह ऐप आपको मेट्रिक और इम्पीरियल यूनिट्स के बीच सटीक रूपांतरण में मदद करता है। लंबाई, वजन, आयतन, तापमान, क्षेत्रफल और गति की यूनिट्स को कन्वर्ट करने के लिए विभिन्न कैटेगरी टैब्स का चयन करें। रियल-टाइम द्विदिशीय रूपांतरण आपको किसी भी तरफ मान दर्ज करने की अनुमति देता है और दूसरी तरफ स्वचालित रूप से रूपांतरण परिणाम प्रदर्शित करता है।',
            github: 'https://github.com/muhac/what-the-imperial-units',
            githubLabel: 'GitHub रिपॉजिटरी:',
        },
    },
    ru: {
        cardTitle: 'Конвертер единиц',
        metricLabel: 'Метрическая',
        imperialLabel: 'Имперская',
        langLabel: 'Язык',
        tabs: {
            length: 'Длина / Расстояние',
            weight: 'Вес / Масса',
            volume: 'Объём',
            area: 'Площадь',
            speed: 'Скорость',
            temperature: 'Температура',
        },
        unitLabels: {
            centimeters: 'Сантиметры (см)',
            meters: 'Метры (м)',
            kilometers: 'Километры (км)',
            inch: 'Дюйм (in)',
            foot: 'Фут (ft)',
            yard: 'Ярд (yd)',
            mile: 'Миля (mi)',
            grams: 'Граммы (г)',
            kilograms: 'Килограммы (кг)',
            ounce: 'Унция (oz)',
            pound: 'Фунт (lb)',
            liters: 'Литры (л)',
            milliliters: 'Миллилитры (мл)',
            gallon: 'Галлон (gal)',
            fluid_ounce: 'Жидкая унция (fl oz)',
            pint: 'Пинта (pt)',
            quart: 'Кварта (qt)',
            celsius: 'Цельсий (°C)',
            fahrenheit: 'Фаренгейт (°F)',
            square_meters: 'Квадратные метры (м²)',
            hectares: 'Гектары (га)',
            square_kilometers: 'Квадратные километры (км²)',
            square_feet: 'Квадратные футы (ft²)',
            acres: 'Акры (ac)',
            square_miles: 'Квадратные мили (mi²)',
            kilometers_per_hour: 'Километры в час (км/ч)',
            miles_per_hour: 'Мили в час (mph)',
            meters_per_second: 'Метры в секунду (м/с)',
            feet_per_second: 'Футы в секунду (ft/s)',
            knots: 'Узлы (kn)',
        },
        infoCard: {
            title: 'Советы по конвертации',
            description: 'Это приложение поможет вам точно конвертировать между метрическими и имперскими единицами. Выберите различные категории вкладок для конвертации единиц длины, веса, объёма, температуры, площади и скорости. Двунаправленная конвертация в реальном времени позволяет вводить значения с любой стороны и автоматически отображает результаты конвертации с другой стороны.',
            github: 'https://github.com/muhac/what-the-imperial-units',
            githubLabel: 'GitHub репозиторий:',
        },
    },
};
